import { createContext, useContext, useEffect, useMemo, useCallback, ReactNode } from 'react';
import getSocket from 'services/socket';
import { useIsUserEditingVehicleQuery } from 'services/api';
import { useCurrentUser } from 'hooks/selectors';
import { Vehicle } from 'types/vehicle';

interface VehicleEditingContextValue {
  hasAnotherEditor: boolean;
  warningText: string | null;
}

const VehicleEditingContext = createContext<VehicleEditingContextValue | null>(null);

export const useVehicleEditing = () => {
  const ctx = useContext(VehicleEditingContext);
  if (!ctx) {
    return { hasAnotherEditor: false, warningText: '' };
  }
  return ctx;
};

interface Props {
  vehicle: Vehicle;
  children: ReactNode;
}

export const VehicleEditingProvider = ({ vehicle, children }: Props) => {
  const socket = getSocket();
  const user = useCurrentUser();

  const { data: editingUser, refetch } = useIsUserEditingVehicleQuery(vehicle.id);

  const socketListener = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!vehicle || vehicle.status === 'draft') return;

    const channel = `${vehicle.id}:${user.id}`;

    socket.emit('subscribe-edit-vehicle', channel);
    socket.on('edit-vehicle:user-left', socketListener);

    return () => {
      socket.off('edit-vehicle:user-left', socketListener);
      socket.emit('unsubscribe-edit-vehicle', channel);
    };
  }, [vehicle.id, vehicle.status, socketListener, user.id]);

  const warningText = useMemo(() => {
    if (!editingUser) return null;
    const name = editingUser.role === 'seller' ? editingUser.dealership_name : editingUser.first_name;

    return `${name} is already editing this vehicle. Please contact them if you want to make changes`;
  }, [editingUser]);

  return (
    <VehicleEditingContext.Provider
      value={{
        hasAnotherEditor: !!editingUser,
        warningText
      }}
    >
      {children}
    </VehicleEditingContext.Provider>
  );
};
